import { v2, v3, quat } from "./short3";
import * as Maths from "oc-commons/Maths";
import { getDirection } from "./Maths2";
import { OBB } from "three/examples/jsm/math/OBB";

import { Box2, Box3, Matrix4, Object3D, Plane, Quaternion, Vector2, Vector3 } from "three";

// statische Hilfsvariablen, die in einigen Methoden benutzt werden
const edge = new Vector3();
const normal = new Vector3();
const EPSILON = 0.000001;
const default_epsilon_orthogonal = Math.cos(((90 - 1) * Math.PI) / 180);
const default_epsilon_parallel = Math.cos((1 * Math.PI) / 180);

export const ORTHOGONAL = 1;
export const PARALLEL = 2;

/**
 * Statische Utility-Klasse zu 3D-Mathematik
 */

/**
 * erzeugt zu einem gegebenen Koordinatensystem (definiert durch y- und z-Achse)
 * eine Matrix, die das Standard-Koordinatensystem in das gegebenen Koordinatensystem transformiert
 */
export function orientationMatrix(yAxis: Vector3, zAxis: Vector3): Matrix4 {
  const xAxis = v3().crossVectors(yAxis, zAxis);
  return new Matrix4().makeBasis(xAxis, yAxis, zAxis);
}

/**
 * verschiebt ein Object und gibt es zurück
 */
export function addPosition(
  object: Object3D,
  xOrVector: Vector3 | number,
  y?: number,
  z?: number,
): Object3D {
  if (object) {
    if (xOrVector instanceof Vector3) {
      object.position.add(xOrVector);
    } else {
      object.position.add(v3(xOrVector, y || 0, z || 0));
    }
  }
  return object;
}

/**
 * Führt Math.sign für jede Komponente des Vectors aus
 */
export function signum(vector3: Vector3): Vector3 {
  vector3.x = Math.sign(vector3.x);
  vector3.y = Math.sign(vector3.y);
  vector3.z = Math.sign(vector3.z);
  return vector3;
}

/**
 * gibt true zurück, wenn der Vector existiert und jede Komponente eine endliche Zahl ist
 */
export function vectorIsFinite(v: Vector3): boolean {
  return !!v && !Maths.notFinite(v.x) && !Maths.notFinite(v.y) && !Maths.notFinite(v.z);
}

/**
 * gibt die minimale Länge einer Dimension eines Vectors zurück
 */
export function minAxis(vector: Vector3): number {
  return Math.min(vector.x, Math.min(vector.y, vector.z));
}

/**
 * gibt die maximale Länge einer Dimension eines Vectors zurück
 */
export function maxAxis(vector: Vector3): number {
  return Math.max(vector.x, Math.max(vector.y, vector.z));
}

/**
 * @deprecated use THREE.Spherical instead!
 * Wandelt einen Vector in Polarkoordinaten um.
 * Spherical Vector in ISO (physical) Convention
 * x: radius
 * y: polar / inclinitation angle (rotation around y axis)
 * z: azimuth angle (height above ground)
 */
export function cartesianToSphericalCoordinates(
  cartesianPoint: Vector3,
  optionalTarget: Vector3,
): Vector3 {
  if (!optionalTarget) {
    optionalTarget = v3();
  }
  const radius = cartesianPoint.length();
  const theta = Math.atan2(cartesianPoint.x, cartesianPoint.z);
  const phi = Math.atan2(
    Math.sqrt(cartesianPoint.x * cartesianPoint.x + cartesianPoint.z * cartesianPoint.z),
    cartesianPoint.y,
  );
  optionalTarget.x = radius;
  optionalTarget.y = theta;
  optionalTarget.z = phi;
  return optionalTarget;
}

/**
 * @deprecated use THREE.Spherical instead!
 * Wandelt Polarkoordinaten in kartesische Koordinaten um,
 * Umkehrfunktion zu cartesianToSphericalCoordinates
 */
export function sphericalCoordinatesToCartesian(
  spericalPoint: Vector3,
  optionalTarget?: Vector3,
): Vector3 {
  if (!optionalTarget) {
    optionalTarget = v3();
  }
  const radius = spericalPoint.x;
  const theta = spericalPoint.y;
  const phi = spericalPoint.z;
  optionalTarget.x = radius * Math.sin(phi) * Math.sin(theta);
  optionalTarget.y = radius * Math.cos(phi);
  optionalTarget.z = radius * Math.sin(phi) * Math.cos(theta);
  return optionalTarget;
}

/**
 * wie vector.lerpVectors aber mit separaten alphas pro Index
 * @param v1 Start-Vector
 * @param v2 Ziel-Vector
 * @param alphaVector jeweils pro Komponente 0-1, gibt an, wie viel der Komponente von v1 in v2 überblendet werden soll
 * @param optionalTarget
 */
export function lerpVectorsPartial(
  v1: Vector3,
  v2: Vector3,
  alphaVector: Vector3,
  optionalTarget?: Vector3,
): Vector3 {
  if (!optionalTarget) {
    optionalTarget = v3();
  }
  optionalTarget.x = v1.x * (1 - alphaVector.x) + v2.x * alphaVector.x;
  optionalTarget.y = v1.y * (1 - alphaVector.y) + v2.y * alphaVector.y;
  optionalTarget.z = v1.z * (1 - alphaVector.z) + v2.z * alphaVector.z;
  return optionalTarget;
}

const fullCircle = 2 * Math.PI;
/**
 * normalisiert den angle (Bogenmaß) im Interval zwischen 0 und 2*Math.PI (360°)
 */
export function normalizeAngle(angleInRadians: number): number {
  return ((angleInRadians % fullCircle) + fullCircle) % fullCircle;
}

/**
 * wandelt einen Winkel vom Grad ins Bogenmaß um
 * Ergebniss zwischen: [-Pi,Pi]
 */
export function angelDegToRad(angelInDeg: number): number {
  return angelInDeg * (Math.PI / 180);
}

/**
 * true, wenn jede Komponente von sizeA <= sizeB ist
 */
export function sizeFitsInto(sizeA: Vector3, sizeB: Vector3): boolean {
  return sizeA.x <= sizeB.x && sizeA.y <= sizeB.y && sizeA.z <= sizeB.z;
}

/**
 * true, wenn das Dreieck a,b,c degeneriert ist, d.h. wenn die Fläche 0 ist bzw. alle Punkte auf einer Linie
 * liegen oder derselbe Punkt sind.
 */
export function isTriangleDegenerated(
  a: Vector3,
  b: Vector3,
  c: Vector3,
  epsilon = EPSILON,
): boolean {
  // TODO gibt es hierfür einen schnelleren Test? Kann man auf EPSILON verzichten?
  normal.subVectors(a, b).cross(edge.subVectors(c, b));
  return normal.lengthSq() < epsilon;
}

/**
 * Erzeugt zu einem gegebenen Einheitsvector zwei dazu orthogonale Einheitsvectoren.
 * The output vectors {U,V} are unit length and mutually perpendicular,
 * and {U,V,W} is an orthonormal basis.
 * from https://github.com/timprepscius/GeometricTools/blob/1623f4962bfaf3d5e4e42622b74664ce1b79ecf3/WildMagic5/LibMathematics/Algebra/Wm5AVector.cpp#L269
 */
export function generateComplementBasis(
  inputVector: Vector3,
  outputVectorBasis1: Vector3,
  outputVectorBasis2: Vector3,
) {
  const vec0 = outputVectorBasis1;
  const vec1 = outputVectorBasis2;
  const vec2 = inputVector;
  let invLength;

  if (Math.abs(vec2.x) >= Math.abs(vec2.y)) {
    // vec2.x or vec2.z is the largest magnitude component, swap them
    invLength = 1.0 / Math.sqrt(vec2.x * vec2.x + vec2.z * vec2.z);
    vec0.x = -vec2.z * invLength;
    vec0.y = 0.0;
    vec0.z = +vec2.x * invLength;
    vec1.x = vec2.y * vec0.z;
    vec1.y = vec2.z * vec0.x - vec2.x * vec0.z;
    vec1.z = -vec2.y * vec0.x;
  } else {
    // vec2.y or vec2.z is the largest magnitude component, swap them
    invLength = 1.0 / Math.sqrt(vec2.y * vec2.y + vec2.z * vec2.z);
    vec0.x = 0.0;
    vec0.y = +vec2.z * invLength;
    vec0.z = -vec2.y * invLength;
    vec1.x = vec2.y * vec0.z - vec2.z * vec0.y;
    vec1.y = -vec2.x * vec0.z;
    vec1.z = vec2.x * vec0.y;
  }
}

/**
 * gibt zu einem vector einen beliebigen orthonormalen Einheitsvector zurück (der also in der Ebene senkrecht dazu liegt)
 */
export function getAnyOrthonormalVector(vector3: Vector3): Vector3 {
  const outputVectorBasis1 = v3();
  const outputVectorBasis2 = v3();
  generateComplementBasis(vector3.clone().normalize(), outputVectorBasis1, outputVectorBasis2);
  return outputVectorBasis1;
}

/**
 * Gibt den Winkel zwischen zweier Vektoren a und b in einer durch normal definierten
 * Ebene zurück, wobei der Winkel im Uhrzeigersinn gemessen wird. Dadurch können auch
 * Winkel größer 180° ermittelt werden, während Vector3.angleTo die Richtungsinformation
 * nicht ermitteln kann (entsprechend nur Winkel von 0° bis 180°).
 */
export function angleClockwise(a: Vector3, b: Vector3, normal: Vector3): number {
  const axb = a.clone().cross(b);
  return Math.atan2(axb.length(), a.dot(b)) * Math.sign(axb.dot(normal));
}

/**
 * prüft, ob zwei Normalenvektoren rechtwinklig oder parallel zueinander stehen.
 * @return 0: weder noch, 1: rechtwinklig, 2: parallel
 */
export function checkOrthogonalOrParallel(
  aNorm: Vector3,
  bNorm: Vector3,
  epsilon_orthogonal = default_epsilon_orthogonal,
  epsilon_parallel = default_epsilon_parallel,
): number {
  const cos = Math.abs(aNorm.dot(bNorm));
  if (cos < epsilon_orthogonal) {
    return ORTHOGONAL;
  }
  if (cos > epsilon_parallel) {
    return PARALLEL;
  }
  return 0;
}

/**
 * stehen zwei Normalenvektoren rechtwinklig zueinander?
 */
export function isOrthogonal(
  aNorm: Vector3,
  bNorm: Vector3,
  epsilon_orthogonal = default_epsilon_orthogonal,
): boolean {
  const cos = Math.abs(aNorm.dot(bNorm));
  return cos < epsilon_orthogonal;
}

/**
 * stehen zwei Normalenvektoren parallel zueinander?
 */
export function isParallel(
  aNorm: Vector3,
  bNorm: Vector3,
  epsilon_parallel = default_epsilon_parallel,
): boolean {
  const cos = Math.abs(aNorm.dot(bNorm));
  return cos > epsilon_parallel;
}

/**
 * Formt einen Vector in einen Normalenvektor einer Box um, und zwar in den Normalenvektor der Box-Seite,
 * welche der verlängerte Vector schneidet. Der Vector wird dabei als Ortsvektor und die Box als Einheitsbox
 * mit dem Koordinatenursprung im Zentrum gedacht.
 */
export function nearestBoxFaceNormal(boxVector: Vector3): Vector3 {
  const v = boxVector.clone().normalize();
  for (let i = 0; i < 3; i++) {
    const x = v.getComponent(i);
    v.setComponent(i, x < -0.5 ? -1 : x > 0.5 ? +1 : 0);
  }
  return v;
}

/**
 * like Matrix4.lookAt, but with direction instead of eye and target
 */
export function lookAtMatrix(matrix: Matrix4, lookDirection: Vector3, up: Vector3): Matrix4 {
  const x = new Vector3();
  const y = new Vector3();
  const z = lookDirection.clone();
  const te = matrix.elements;
  if (z.lengthSq() === 0) {
    z.z = 1;
  }
  z.normalize();
  x.crossVectors(up, z);
  if (x.lengthSq() === 0) {
    if (Math.abs(up.z) === 1) {
      z.x += 0.0001;
    } else {
      z.z += 0.0001;
    }
    z.normalize();
    x.crossVectors(up, z);
  }
  x.normalize();
  y.crossVectors(z, x);
  te[0] = x.x;
  te[4] = y.x;
  te[8] = z.x;
  te[1] = x.y;
  te[5] = y.y;
  te[9] = z.y;
  te[2] = x.z;
  te[6] = y.z;
  te[10] = z.z;
  return matrix;
}

/**
 * Decompose the rotation on to 2 parts:
 *
 * 1. Twist - rotation around the "direction" vector
 * 2. Swing - rotation around axis that is perpendicular to "direction" vector
 *
 * The rotation can be composed back by
 * rotation = swing * twist
 *
 * It has a singularity in case of swing close to 180 degrees rotation.
 * If the input quaternion is of non-unit length, the outputs are non-unit as well,
 * otherwise, outputs are both unit.
 *
 * from https://stackoverflow.com/questions/3684269/component-of-a-quaternion-rotation-around-an-axis
 *
 * @param {THREE.Quaternion} rotation
 * @param {THREE.Vector3} direction
 * @param {*} [swing=new THREE.Quaternion()] optional output, for reusing
 * @param {*} [twist=new THREE.Quaternion()] optional output, for reusing
 * @returns {{swing: THREE.Quaternion, twist: THREE.Quaternion}}
 */
export function swingTwistDecomposition(
  rotation: Quaternion,
  direction: Vector3,
  swing = new Quaternion(),
  twist = new Quaternion(),
): { swing: Quaternion; twist: Quaternion } {
  const p = new Vector3(rotation.x, rotation.y, rotation.z);
  p.projectOnVector(direction);
  if (vectorIsFinite(p)) {
    twist.set(p.x, p.y, p.z, rotation.w);
    twist.normalize();
    swing.copy(rotation).multiply(twist.clone().invert());
  }
  return { swing, twist };
}

/**
 * Berechnet den Mittelpunkt aus n vektoren
 */
export function middle(vectors: Vector3[]): Vector3 {
  if (vectors.length < 3) {
    return centerOfGravity(vectors);
  }
  return new Box3().setFromPoints(vectors).getCenter(v3());
}

/**
 * Berechnet den Schwerpunkt aus n vektoren
 */
export function centerOfGravity(vectors: Vector3[]): Vector3 {
  const m = v3();
  vectors.forEach((v) => m.add(v));
  m.multiplyScalar(1 / vectors.length);
  return m;
}

/**
 * Prüft, ob zwei Vectoren kolinear sind (d.h. auf einer Linie liegen)
 */
export function colinear(a: Vector3, b: Vector3, epsilonDegree = 0.01): boolean {
  const angle = (a.angleTo(b) * 180) / Math.PI;
  return Math.abs(angle) < epsilonDegree || Math.abs(180 - angle) < epsilonDegree;
}

/**
 * Entfernt kolineare Zwischenpunkte, behält aber die Endpunkte des Pfades bei
 */
export function removeCollinearPoints(path: Vector3[], epsilonDegree = 0.01): Vector3[] {
  if (path.length < 3) {
    return path;
  }
  const result = [path[0]];
  for (let i = 1; i < path.length - 1; i++) {
    const prev = path[i - 1];
    const curr = path[i];
    const next = path[i + 1];
    const d1 = getDirection(v3To2xy(prev), v3To2xy(curr));
    const d2 = getDirection(v3To2xy(curr), v3To2xy(next));
    if (!colinear(v2To3(d1), v2To3(d2), epsilonDegree)) {
      result.push(curr);
    }
  }
  result.push(path[path.length - 1]);
  return result;
}

/**
 * berechnet die Größe der BoundingBox um eine Punktwolke (2d oder 3d)
 */
export function getBoxSizeOfPoints(points: Vector3[] | Vector2[]): Vector3 | Vector2 | number {
  if (points == null || points.length == 0) {
    return 0;
  }
  if ((points[0] as any).isVector2) {
    return new Box2().setFromPoints(points as Vector2[]).getSize(v2());
  } else {
    return new Box3().setFromPoints(points as Vector3[]).getSize(v3());
  }
}

/**
 * convertiert eine Box2 zu Box3
 */
export function box2To3(box: Box2, z = 1): Box3 {
  return new Box3(
    v3(box.min.x, Math.min(box.min.y, box.max.y), -z),
    v3(box.max.x, Math.max(box.min.y, box.max.y), z),
  );
}

/**
 * convertiert eine Box3 zu Box2
 */
export function box3To2(box: Box3): Box2 {
  return new Box2(v3To2xy(box.min), v3To2xy(box.max));
}

/**
 * convertiert einen v2 zu v3
 */
export function v2To3(v: Vector2, z = 0): Vector3 {
  return v3(v.x, v.y, z);
}

/**
 * convertiert einen v2 zu v3
 */
export function v2To3xy(v: Vector2, z = 0): Vector3 {
  return v3(v.x, v.y, z);
}

/**
 * convertiert einen v2 zu v3
 */
export function v2To3xz(v: Vector2, y = 0): Vector3 {
  return v3(v.x, y, v.y);
}

/**
 * convertiert einen v3 zu v2(x,y)
 */
export function v3To2xy(v: Vector3): Vector2 {
  return v2(v.x, v.y);
}

/**
 * convertiert einen v3 zu v2(x,z)
 */
export function v3To2xz(v: Vector3): Vector2 {
  return v2(v.x, v.z);
}

/**
 * wandelt ein beliebiges Objekt mit x,y,z Properties in einen THREE.Vector3 um
 */
export function toV3(something: Vector3 | { x?: number; y?: number; z?: number } | null): Vector3 {
  return something instanceof Vector3
    ? something
    : new Vector3(something?.x ?? 0, something?.y ?? 0, something?.z ?? 0);
}

/**
 * erzeugt eine Box3 von einer grid koordinate (gridCenter) und einer size
 */
export function boxFromGridMinAndSize(
  gridCenter: Vector3,
  size: Vector3,
  target = new Box3(),
): Box3 {
  target.min.copy(size).multiply(gridCenter).negate();
  target.max.copy(target.min).add(size);
  return target;
}

/**
 * erzeugt eine Matrix4, welche einer Drehung um einen Punkt und eine dadurch gehende Achse um einen Winkel entspricht.
 * Die Matrix kann anschließend mit der Matrix eines Objektes multipliziert werden.
 */
export function createPivotMatrix(pivotPoint: Vector3, axis: Vector3, angle: number): Matrix4 {
  const translate0 = new Matrix4().makeTranslation(pivotPoint.x, pivotPoint.y, pivotPoint.z);
  const translate1 = new Matrix4().makeTranslation(-pivotPoint.x, -pivotPoint.y, -pivotPoint.z);
  const rotation = new Matrix4();
  return new Matrix4()
    .multiply(translate0)
    .multiply(rotation.makeRotationAxis(axis, angle))
    .multiply(translate1);
}

/**
 * wandelt alle negativen in positive Komponenten um
 */
export function abs(v: Vector3): Vector3 {
  return v3(Math.abs(v.x), Math.abs(v.y), Math.abs(v.z));
}

export interface RayWithOriginDirection {
  origin: Vector3;
  direction: Vector3;
}

/**
 * gibt den Abstand der Projektion eines Punktes auf einen Ray zum Ray.origin an,
 * wobei in Richtung des Rays positiv, sonst negativ gezählt wird
 */
export function distanceToRay(ray: RayWithOriginDirection, point: Vector3): number {
  return ray.direction.dot(point.clone().sub(ray.origin));
}

/**
 * Wandelt eine lokale box3 und matrixWorld zu einer OBB
 */
export function box3AndMatrixToOBB(box3: Box3, matrix4: Matrix4): OBB {
  return new OBB().fromBox3(box3).applyMatrix4(matrix4);
}

/**
 * Wandelt eine OBB zu einer lokalen box3 mittels einer matrixWorld (diese wird invertiert)
 */
export function obbAndMatrixToBox3(obb: OBB, matrixWorld: Matrix4): Box3 {
  const im = matrixWorld.clone().invert();
  const corners = obb.getCorners().map((p) => p.applyMatrix4(im));
  return new Box3().setFromPoints(corners);
}

/**
 * Standardvectoren
 */
export const vector = {
  zero: v3(0, 0, 0),
  x: v3(1, 0, 0),
  y: v3(0, 1, 0),
  z: v3(0, 0, 1),
  one: v3(1, 1, 1),
  xneg: v3(-1, 0, 0),
  yneg: v3(0, -1, 0),
  zneg: v3(0, 0, -1),
  oneneg: v3(-1, -1, -1),
};

/**
 * Standardplanes
 */
export const plane = {
  x0: new Plane().setFromNormalAndCoplanarPoint(vector.x, vector.zero),
  y0: new Plane().setFromNormalAndCoplanarPoint(vector.y, vector.zero),
  z0: new Plane().setFromNormalAndCoplanarPoint(vector.z, vector.zero),
};

/**
 * Standardquaternions
 */
export const quaternion = {
  zero: new Quaternion(0, 0, 0, 1),
  x180: new Quaternion(1, 0, 0, 0),
  y180: new Quaternion(0, 1, 0, 0),
  z180: new Quaternion(0, 0, 1, 0),
  x45: quat(1, 0, 0, 45),
  y45: quat(0, 1, 0, 45),
  z45: quat(0, 0, 1, 45),
  x90: quat(1, 0, 0, 90),
  y90: quat(0, 1, 0, 90),
  z90: quat(0, 0, 1, 90),
  x270: quat(1, 0, 0, 270),
  y270: quat(0, 1, 0, 270),
  z270: quat(0, 0, 1, 270),
};

export const matrix = {
  identity: new Matrix4(),
};

export const box = {
  zero: new Box3(v3(0, 0, 0), v3(0, 0, 0)),
  one: new Box3(v3(0, 0, 0), v3(1, 1, 1)),
};
